import React, {useCallback, useEffect, useMemo, useState} from 'react'
import {shallowEqual, useDispatch, useSelector} from 'react-redux'
import {RootState} from '../../../../setup'
import {fetchAll} from '../redux/InventoryAction'
import {Link} from 'react-router-dom'
import {KTSVG} from '../../../../_metronic/helpers/components/KTSVG'
import {Route, Breadcrumbs} from '../../../components/Breadcrumbs'
import {Can} from '../../../rbac/Can'
import {
  InventorySearchModelInitValues,
  InventoryTableModelInitValues,
} from '../models/InventoryModel'
import {Table} from '../../../components/ReactTable'
import {formatDateTime, formatNumber} from '../../../helper/index'
import {Field, Form, Formik} from 'formik'
import * as Yup from 'yup'
import 'rsuite/dist/rsuite.css'

type Props = {
  className: string
}

export const defaultColDef = {
  resizable: true,
  initialWidth: 100,
  filter: true,
  floatingFilter: true,
  suppressMovable: true,
  suppressKeyboardEvent: (params: any) => params.editing,
}

export const Inventories: React.FC<Props> = ({className}) => {
  const dispatch = useDispatch()
  const inventories: any = useSelector<RootState>(
    ({inventories}) => inventories?.inventories,
    shallowEqual
  )

  const loading: any = useSelector<RootState>(({inventories}) => inventories.loading, shallowEqual)
  const pages: any = useSelector<RootState>(
    ({inventories}) => inventories.pagination?.pages,
    shallowEqual
  )
  const [searchParams, setSearchParams]: any = useState({})

  const [show, setShow] = useState(false)
  const filterHandel = () => {
    setShow(!show)
  }

  useEffect(() => {
    dispatch(fetchAll(InventoryTableModelInitValues))
  }, [dispatch])

  const fetchData = useCallback(
    ({pageSize, pageIndex, params, sortBy}: any) => {
      const queryParams = {
        page: pageIndex + 1,
        sortBy: sortBy[0]?.id || 'createdAt',
        sortOrder: sortBy[0]?.desc === false ? 'asc' : 'desc',
        search: params,
      }
      dispatch(fetchAll(queryParams))
    },
    [searchParams]
  )

  const columns = useMemo(
    () => [
      {
        Header: 'Created Date',
        accessor: 'createdAt',
        Cell: (cellInfo: any) => {
          if (cellInfo?.row?.original?.createdAt) {
            return formatDateTime(cellInfo?.row?.original?.createdAt)
          } else {
            return '-'
          }
        },
        show: true,
      },
      {Header: 'Inventory Type', accessor: 'type', show: true},
      {
        Header: 'Reference',
        accessor: 'reference',

        cellStyle: {textAlign: 'left'},
        show: true,
      },
      {
        Header: 'Location',
        accessor: 'location.name',
        disableSortBy: true,
        show: true,
      },
      {
        Header: 'Product',
        accessor: 'product.name',
        disableSortBy: true,
        show: true,
      },
      {
        Header: 'Expiry Date',
        accessor: 'expiryDate',
        Cell: (cellInfo: any) => {
          if (cellInfo?.row?.original?.expiryDate) {
            return formatDateTime(cellInfo?.row?.original?.expiryDate)
          } else {
            return '-'
          }
        },
        show: true,
      },
      {
        Header: 'UOM',
        accessor: 'product.UOM',
        disableSortBy: true,

        show: true,
      },

      {
        Header: 'Quantity',
        accessor: 'quantity',
        Cell: (cellInfo: any) => {
          if (cellInfo?.row?.original?.quantity) {
            return formatNumber(cellInfo?.row?.original?.quantity)
          } else {
            return '0.00'
          }
        },
        show: true,
      },
      {
        Header: 'Quantity In Stock',
        accessor: 'quantityInStock',
        Cell: (cellInfo: any) => {
          if (cellInfo?.row?.original?.quantityInStock) {
            return formatNumber(cellInfo?.row?.original?.quantityInStock)
          } else {
            return '0.00'
          }
        },
        show: true,
      },
    ],
    []
  )

  const routes: Route[] = [
    {name: 'Dashboard', url: '/dashboard'},
    {name: 'Inventories List', url: '/inventory'},
  ]

  return (
    <>
      <div className={`card card-custom ${className}`}>
        <Breadcrumbs routes={routes} />
        <div className='card-header border-0 pt-5'>
          <h1 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>Inventories List</span>
          </h1>
          <div className='d-flex'>
            <Can do='create' on='inventory'>
              <div
                className='card-toolbar'
                data-bs-toggle='tooltip'
                data-bs-placement='top'
                data-bs-trigger='hover'
                title='Add Inventory'
              >
                <Link to='/inventory/add' className='btn btn-sm btn-light-primary m-2'>
                  <KTSVG path='/media/icons/duotune/arrows/arr075.svg' className='svg-icon-3' />
                  Add Inventory
                </Link>
              </div>
            </Can>
            <Can do='read' on='inventory'>
              <div
                className='card-toolbar'
                data-bs-toggle='tooltip'
                data-bs-placement='top'
                data-bs-trigger='hover'
                title='Inventory Report'
              >
                <Link to='/inventory/report' className='btn btn-sm btn-light-primary'>
                  <KTSVG path='/media/icons/duotune/files/fil001.svg' className='svg-icon-3' />
                  Inventory Report
                </Link>
              </div>
            </Can>
          </div>
        </div>
        <div className='card-body'>
          <div
            className={`d-flex align-items-center ${
              !show ? 'justify-content-end' : 'justify-content-between'
            }`}
          >
            <p className='my-0 me-3' style={{color: '#004B87', fontSize: '14px'}}>
              <b>Search Filters</b>
            </p>
            <span onClick={filterHandel}>
              <img src='/media/svg/icons/General/filter.svg' alt='' />
            </span>
          </div>
          {show ? (
            <Formik
              initialValues={InventorySearchModelInitValues}
              validationSchema={Yup.object({
                type: Yup.string(),
                quantity: Yup.string(),
                quantityInStock: Yup.string(),
              })}
              onSubmit={(values: any) => {
                if (values.type === 'Inventory Type') {
                  values.type = ''
                }
                setSearchParams(values)
              }}
            >
              {({values, setFieldValue}) => (
                <Form>
                  <div className='row row-cols-lg-2 row-cols-md-2 row-cols-1 g-5 mt-1'>
                    <div className='form-group col'>
                      <label htmlFor='type' className='fw-bold'>
                        Inventory Type
                      </label>
                      <Field
                        as='select'
                        className='form-select form-select-md'
                        data-control='select2'
                        name='type'
                      >
                        <option>Inventory Type</option>
                        <option value='INCREASE ADJUSTMENT'>INCREASE ADJUSTMENT</option>
                        <option value='DECREASE ADJUSTMENT'>DECREASE ADJUSTMENT</option>
                        <option value='MATERIAL ISSUANCE'>MATERIAL ISSUANCE</option>
                        <option value='GOODS RECEIVED'>GOODS RECEIVED</option>
                        <option value='MATERIAL RETURN'>MATERIAL RETURN</option>
                        <option value='PURCHASE RETURN'>PURCHASE RETURN</option>
                      </Field>
                    </div>
                    <div className='form-group col'>
                      <label htmlFor='reference' className='fw-bold'>
                        Reference
                      </label>
                      <Field
                        name='reference'
                        placeholder='Reference'
                        className='form-control form-control-lg'
                      />
                    </div>
                  </div>

                  <div className='row row-cols-lg-2 row-cols-md-2 row-cols-1 g-5 mt-1'>
                    <div className='form-group col'>
                      <label htmlFor='quantity' className='fw-bold'>
                        Quantity
                      </label>
                      <Field
                        name='quantity'
                        placeholder='Quantity'
                        className='form-control form-control-lg'
                      />
                    </div>
                    <div className='form-group col'>
                      <label htmlFor='quantityInStock' className='fw-bold'>
                        Quantity In Stock
                      </label>
                      <Field
                        name='quantityInStock'
                        placeholder='Quantity In Stock'
                        className='form-control form-control-lg'
                      />
                    </div>
                  </div>
                  <div className='d-flex justify-content-end mt-10'>
                    <Link
                      to='/inventory'
                      className='btn btn-sm btn-light-primary me-3'
                      data-bs-toggle='tooltip'
                      data-bs-placement='top'
                      title='Cancel'
                      onClick={() => {
                        setSearchParams(InventorySearchModelInitValues)
                        setFieldValue('reference', '')
                        setFieldValue('type', '')
                        setFieldValue('quantity', '')
                        setFieldValue('quantityInStock', '')
                      }}
                    >
                      <span className='indicator-label'>Cancel</span>
                    </Link>

                    <button
                      type='submit'
                      className='btn btn-sm btn-primary'
                      data-bs-toggle='tooltip'
                      data-bs-placement='top'
                      title='Search'
                      id='liveToastBtn'
                    >
                      <span className='indicator-label'>Search</span>
                    </button>
                  </div>
                </Form>
              )}
            </Formik>
          ) : null}
          <div className='d-flex mt-5 '>
            <div className='react-table-wrapper' style={{width: '100%'}}>
              <Table
                columns={columns}
                data={inventories}
                params={searchParams}
                pageCount={pages}
                fetchData={fetchData}
                loading={loading}
              />
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
